import React, { useEffect, useState } from 'react';
import { Box, Paper, Typography, TextField, MenuItem, CircularProgress } from '@mui/material';
import { Row, Col } from 'antd';
import { useSnackbar } from 'notistack';
import { getAllBuiltInRules, getBuiltInRuleById } from "../../../../../api's/DataModelApi";
import LoadingIcon from '../../../../../reusable-component/LoadingIcon';

const BuiltInConfigure = ({ entityId, formData, setFormData }) => {
  const [rules, setRules] = useState([]);
  const [loading, setLoading] = useState(false);
  const [ruleDetails, setRuleDetails] = useState(null);
  const { enqueueSnackbar } = useSnackbar();

  // 🔹 Fetch rule list
  useEffect(() => {
    const fetchRules = async () => {
      try {
        setLoading(true);
        const data = await getAllBuiltInRules();
        setRules(data || []);
      } catch (error) {
        console.error('Error fetching builtin rules:', error);
        enqueueSnackbar('Failed to load rules list', { variant: 'error' });
      } finally {
        setLoading(false);
      }
    };
    fetchRules();
  }, [enqueueSnackbar]);

  // 🔹 When user selects a built-in rule
  const handleRuleSelect = async (e) => {
    const selectedRuleId = e.target.value;

    try {
      setLoading(true);
      const details = await getBuiltInRuleById(selectedRuleId, entityId);
      setRuleDetails(details);

      setFormData((prev) => ({
        ...prev,
        rule_type_id: selectedRuleId,
        rule_name: details.rule_name,
        configuration: {
          ...prev.configuration,
          ...details.configuration,
          applies_to: prev.configuration.applies_to,
          case_sensitive: false,
          apply_to_nulls: false,
        },
      }));
    } catch (error) {
      console.error('Error fetching rule details:', error);
      if (error?.response?.data?.message) {
        enqueueSnackbar(error?.response?.data?.message, { variant: 'error' });
      } else {
        enqueueSnackbar('Failed to load rule details', { variant: 'error' });
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <Paper elevation={2} sx={{ p: 4, borderRadius: 3, backgroundColor: '#fafafa' }}>
      <Row gutter={[32, 24]} align="top">
        <Col xs={24} md={12}>
          <Typography variant="h6" sx={{ mb: 2, fontWeight: 600, color: '#333' }}>
            Built-in Rule
          </Typography>

          <Box display="flex" flexDirection="column" gap={3}>
            {/* Rule Dropdown */}
            <TextField
              select
              label="Select Rule"
              variant="outlined"
              fullWidth
              value={formData.rule_type_id || ''}
              onChange={handleRuleSelect}
              InputProps={{ sx: { borderRadius: 2, backgroundColor: 'white' } }}
            >
              {loading ? (
                <MenuItem disabled>
                  <CircularProgress size={20} />
                </MenuItem>
              ) : (
                rules.map((rule) => (
                  <MenuItem key={rule.rule_id} value={rule.rule_id}>
                    {rule.rule_name}
                  </MenuItem>
                ))
              )}
            </TextField>

            {/* Configuration */}
            {ruleDetails?.configuration && (
              <Box>
                <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600, color: '#444' }}>
                  Configuration
                </Typography>
                <TextField
                  multiline
                  fullWidth
                  minRows={6}
                  value={JSON.stringify(ruleDetails.configuration, null, 2)}
                  InputProps={{
                    readOnly: true,
                    sx: {
                      borderRadius: 2,
                      backgroundColor: '#f5f5f5',
                      fontFamily: 'monospace',
                    },
                  }}
                />
              </Box>
            )}

            {/* Preview Mapping */}
            {ruleDetails?.preview && (
              <Box>
                <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600, color: '#444' }}>
                  Preview Mapping (input → output)
                </Typography>
                <TextField
                  fullWidth
                  multiline
                  minRows={3}
                  value={ruleDetails.preview}
                  InputProps={{
                    readOnly: true,
                    sx: {
                      borderRadius: 2,
                      backgroundColor: '#f0f6ff',
                      fontFamily: 'monospace',
                    },
                  }}
                />
              </Box>
            )}
          </Box>
        </Col>

        {/* Right Section */}
        <Col xs={24} md={12}>
          <Paper
            elevation={0}
            sx={{
              p: 3,
              borderRadius: 2,
              backgroundColor: '#f5f8ff',
              border: '1px solid #dbe3ff',
            }}
          >
            <Typography variant="h6" sx={{ mb: 1.5, fontWeight: 600, color: '#2a3eb1' }}>
              About this recipe
            </Typography>
            <Typography variant="body1" sx={{ color: '#444', lineHeight: 1.8, whiteSpace: 'pre-wrap' }}>
              {ruleDetails?.description || 'Select a recipe to view its description here.'}
            </Typography>
          </Paper>
        </Col>
      </Row>
      {loading && <LoadingIcon />}
    </Paper>
  );
};

export default BuiltInConfigure;
